/**
 * Panda CSS styles for HALO Card component
 *
 * Uses Class Variance Authority (cva) for systematic variant generation
 * Implements 8px border radius, 1px border, and flexible padding variants
 */

import { css, cva } from '@/styled-system/css';

/**
 * Base card styles shared across all variants
 * Implements core visual specifications from requirements
 */
export const cardBase = css({
  borderRadius: '8px', // Per specification
  borderWidth: '1px',
  borderStyle: 'solid',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  outline: 'none',
});

/**
 * Card variant styles using Class Variance Authority (cva)
 * Systematic approach to variant combinations
 */
export const cardVariants = cva({
  variants: {
    variant: {
      default: {
        backgroundColor: 'hpxWhite', // Per specification
        borderColor: 'hpxGrey400', // Per specification
      },
      accent: {
        backgroundColor: 'hpxYellow400', // Per specification for accent cards
        borderColor: 'transparent', // No border for accent variant
      },
      minimal: {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      },
    },

    size: {
      sm: {
        padding: '1x', // 8px - compact layouts
      },
      md: {
        padding: '2x', // 16px - default per specification
      },
      lg: {
        padding: '3x', // 24px - spacious layouts
      },
    },

    collapsible: {
      true: {
        // Additional styles for interactive cards
        cursor: 'default',
      },
      false: {},
    },
  },

  defaultVariants: {
    variant: 'default',
    size: 'md',
    collapsible: false,
  },
});

/**
 * Card header styles with flexible layout variants
 * Replaces fixed cardHeader with CVA system for layout flexibility
 */
export const cardHeaderVariants = cva({
  base: {
    display: 'flex',
    gap: '1x', // 8px spacing between elements
    marginBottom: '2x', // 16px separation from body
    minHeight: '24px', // Ensure consistent height even without content
  },
  variants: {
    layout: {
      default: { justifyContent: 'flex-start' },
      'space-between': { justifyContent: 'space-between' },
      'space-around': { justifyContent: 'space-around' },
      'flex-start': { justifyContent: 'flex-start' },
      'flex-end': { justifyContent: 'flex-end' },
    },
    align: {
      'flex-start': { alignItems: 'flex-start' },
      center: { alignItems: 'center' },
      'flex-end': { alignItems: 'flex-end' },
      baseline: { alignItems: 'baseline' },
    },
  },
  defaultVariants: {
    layout: 'default',
    align: 'center', // Maintains backward compatibility
  },
});

/**
 * Legacy cardHeader for backward compatibility
 * @deprecated Use cardHeaderVariants with default variants instead
 */
export const cardHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: '1x',
  marginBottom: '2x',
  minHeight: '24px',
});

/**
 * Card body styles
 * Flexible content area with collapsible behavior
 */
export const cardBody = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1, // Take remaining space

  '&[data-collapsed="true"]': {
    display: 'none', // Hidden when collapsed for progressive enhancement
  },
});

/**
 * Card title styles with size variants
 * Implements typography specifications
 */
export const cardTitle = css({
  fontSize: 'lg', // 20px - default per specification
  fontWeight: '700', // Per specification
  color: 'hpxBlue500', // Per specification
  margin: '0',
  lineHeight: '1.2',

  '&[data-size="md"]': {
    fontSize: 'md', // 16px variant for smaller titles
  },
});

/**
 * Card icon wrapper styles
 * Proper spacing and alignment for icons
 */
export const cardIcon = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 'md', // 16px - balanced with title
  lineHeight: 1,
  color: 'hpxBlue500', // Match title color
});

/**
 * Collapse toggle button styles
 * Interactive caret button for collapsible functionality
 */
export const cardCollapseToggle = css({
  marginLeft: 'auto', // Push to right side of header
  cursor: 'pointer',
  padding: 'xs', // 4px - minimal touch target padding
  borderRadius: '4px',
  border: 'none',
  backgroundColor: 'transparent',
  color: 'hpxBlue500',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'md',
  transitionProperty: 'background-color',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'ease-in-out',
  minWidth: '24px',
  minHeight: '24px',

  _hover: {
    backgroundColor: 'hpxGrey100',
  },

  _focusVisible: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '2px',
  },

  _active: {
    backgroundColor: 'hpxGrey400',
  },
});

/**
 * Card footer styles
 * Optional footer area for actions or additional content
 */
export const cardFooter = css({
  marginTop: '2x', // 16px separation from body
  display: 'flex',
  flexDirection: 'column',
});
